import moment from 'moment';
import ApiRepository from '../services/api/apiRepository';

// Read signatures are cached for 30 minutes (they are valid for 60 minutes).
const SIGNATURE_STALE_TIME = 1000 * 60 * 30;

interface CachedSignature {
  promise: Promise<string>;
  fetchedAt: number;
}

const signatureCache = new Map<string, CachedSignature>();

/**
 * Returns the container name of a private Azure blob storage url,
 * or an empty string if the url does not point to the storage account.
 */
export const getFileContainerFromUrl = (url: string): string => {
  const match = url?.match(
    /https:\/\/enpalcrftfmt[^.]+\.blob\.core\.windows\.net\/([^/]+)/
  );
  return match ? match[1] : '';
};

export const removeSignatureFromFileUrl = (url: string): string =>
  url.split(/[?#]/)[0];

export const getFileUrlWithSignature = (
  url: string,
  signature?: string
): string =>
  url && signature ? `${removeSignatureFromFileUrl(url)}${signature}` : url;

/**
 * Returns the cached read signature (SAS token query string) of the container
 * or fetches a new one if it is missing or stale.
 */
export const getStorageAccessSignature = (
  containerName: string
): Promise<string> => {
  const cached = signatureCache.get(containerName);
  if (cached && Date.now() - cached.fetchedAt < SIGNATURE_STALE_TIME) {
    return cached.promise;
  }
  const promise = ApiRepository.getStorageAccessSignature(containerName).then(
    (result: any) => result?.response as string
  );
  signatureCache.set(containerName, { promise, fetchedAt: Date.now() });
  // Do not cache failures, so that the next call retries.
  promise.catch(() => signatureCache.delete(containerName));
  return promise;
};

/**
 * Signs the url with a read signature if it points to a private container.
 * Urls outside of the storage account are returned unchanged.
 */
export const getSignedFileUrl = async (url: string): Promise<string> => {
  const containerName = getFileContainerFromUrl(url);
  if (!containerName) return url;
  try {
    const signature = await getStorageAccessSignature(containerName);
    return getFileUrlWithSignature(url, signature);
  } catch {
    return url;
  }
};

const getFileExtension = (file: Blob & { name?: string }): string => {
  const fromName = file.name?.includes('.')
    ? file.name.split('.').pop()
    : undefined;
  const extension = fromName ?? file.type?.split('/').pop();
  return extension ? `.${extension.toLowerCase()}` : '';
};

/**
 * Uploads the file directly to the private container using a SAS upload link.
 * Returns the url of the uploaded file without the signature.
 */
export const uploadFileToContainer = async (
  containerName: string,
  file: Blob & { name?: string }
): Promise<string> => {
  const result: any = await ApiRepository.getStorageUploadLink(containerName);
  const fileName = `${moment.now()}-${crypto.randomUUID()}${getFileExtension(
    file
  )}`;
  const urlSigned = (result?.response as string).replace(
    'FILENAME_PLACEHOLDER',
    encodeURIComponent(fileName)
  );
  await ApiRepository.uploadFile(urlSigned, file);
  return removeSignatureFromFileUrl(urlSigned);
};
